import React, {useEffect, useMemo, useRef, useState} from "react";
import PayCustomerOrder, {PayCustomerOrderSteps} from "../../app-specific/pay-customer-order";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Row} from "reactstrap";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import useAccessLevels from "../../../hooks/use-access-levels";
import ToastService from "../../../../core/services/toast";

const PayForOrderDialog = ({
                               open,
                               close,
                               pay,
                               customerId,
                               remainingBalance,
                               minChargeAmount,
                               showPreAuthMargin,
                           }) => {
    const [step, setStep] = useState(PayCustomerOrderSteps.methodSelection);
    const [submitting, setSubmitting] = useState(false);
    const paymentRef = useRef();
    const operatorAccessLevels = useAccessLevels();

    /**
     * Warns the user when the main API is used in the development environment.
     */
    useEffect(() => {
        if (open) {
            ToastService.mainAPIRedZoneWarning();
        }
    }, [open])

    /**
     * With each change in the open state of the dialog:
     * - if closed, resets the step and submitting state values.
     */
    useEffect(() => {
        if (!open) {
            if (operatorAccessLevels.packages.sales.onlinePayments) {
                setStep(PayCustomerOrderSteps.methodSelection)
            } else {
                setStep(PayCustomerOrderSteps.otherPayments)
            }
            setSubmitting(false);
        }
    }, [open, operatorAccessLevels.packages.sales.onlinePayments])

    const dialogSize = useMemo(() => {
        switch (step) {
            case PayCustomerOrderSteps.methodSelection:
                return 'sm'
            case PayCustomerOrderSteps.onlinePaymentCardSelection:
                return 'md';
            case PayCustomerOrderSteps.onlinePayment:
                return 'sm'
            case PayCustomerOrderSteps.otherPayments:
                return 'sm'
            case PayCustomerOrderSteps.sendEmail:
                return 'sm'
            default:
                return 'sm'
        }
    }, [step])

    const dialogTitle = useMemo(() => {
        switch (step) {
            case PayCustomerOrderSteps.methodSelection:
                return 'Select Payment Method'
            case PayCustomerOrderSteps.onlinePaymentCardSelection:
                return 'Select Credit Card';
            case PayCustomerOrderSteps.sendEmail:
                return "Send Confirmation Email"
            case PayCustomerOrderSteps.onlinePayment:
            case PayCustomerOrderSteps.otherPayments:
            default:
                return 'Pay for Order'
        }
    }, [step])

    const dialogActions = useMemo(() => {
        switch (step) {
            case PayCustomerOrderSteps.methodSelection:
                return []
            case PayCustomerOrderSteps.onlinePaymentCardSelection:
                return [
                    {
                        title: 'Back',
                        onClick: () => setStep(PayCustomerOrderSteps.methodSelection),
                    },
                ];
            case PayCustomerOrderSteps.onlinePayment:
                return [
                    {
                        title: 'Back',
                        onClick: () => setStep(PayCustomerOrderSteps.onlinePaymentCardSelection),
                        disabled: submitting,
                    },
                    {
                        title: submitting ? "Paying..." : 'Proceed',
                        onClick: () => paymentRef.current?.executeAction?.(),
                        disabled: submitting,
                    },
                ];
            case PayCustomerOrderSteps.otherPayments:
                return [
                    {
                        title: 'Back',
                        onClick: () => !operatorAccessLevels.packages.sales.onlinePayments ? close() : setStep(PayCustomerOrderSteps.methodSelection),
                        disabled: submitting,
                    },
                    {
                        title: submitting ? "Paying..." : 'Proceed',
                        onClick: () => paymentRef.current?.executeAction?.(),
                        disabled: submitting,
                    },
                ];
            case PayCustomerOrderSteps.sendEmail:
                return [
                    {
                        title: 'Back',
                        onClick: () => close(),
                        disabled: submitting,
                    },
                    {
                        title: submitting ? "Sending..." : 'Send Email',
                        onClick: () => paymentRef.current?.executeAction?.(),
                        disabled: submitting,
                    },
                ];
            default:
                return []
        }
    }, [step, submitting, operatorAccessLevels.packages.sales.onlinePayments, close])

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={dialogSize}
                className={'bound-dialog'}>
                <DialogTitle>
                    <Row xs={12} className='no-gutters justify-content-center p-2 w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            {dialogTitle}
                        </p>
                    </Row>
                </DialogTitle>
                <DialogContent dividers className={'pt-3'}>
                    <PayCustomerOrder
                        ref={paymentRef}
                        exit={() => close()}
                        pay={pay}
                        step={step}
                        setStep={setStep}
                        customerId={customerId}
                        remainingBalance={remainingBalance}
                        minChargeAmount={minChargeAmount}
                        showPreAuthMargin={showPreAuthMargin}
                        setSubmittingAction={setSubmitting}
                        submittingAction={submitting}
                    />
                </DialogContent>
                {
                    !!dialogActions?.length &&
                    <DialogActions className={'d-flex align-items-center justify-content-between w-100'}>
                        <Button
                            disabled={dialogActions[0].disabled}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => dialogActions[0].onClick()}
                            borderRadius={'0 8px 0 8px'}
                        >
                            {dialogActions[0].title}
                        </Button>
                        {
                            dialogActions.length >= 2 &&
                            <Button
                                disabled={dialogActions[1].disabled}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => dialogActions[1].onClick()}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {dialogActions[1].title}
                            </Button>
                        }
                    </DialogActions>
                }
            </Dialog>
        </>
    )
}

export default PayForOrderDialog;
